import { useState } from "react";
import { signIn, resetPassword, updatePassword } from "./supabaseApi";

const B = {
  red:"#A51C30", black:"#141414", charcoal:"#222222",
  gray:"#4A4A4A", silver:"#8A8A8A", light:"#E8E8E8",
  offWhite:"#F5F4F2", white:"#FFFFFF", green:"#16A34A",
};

const inp = {
  width:"100%", padding:"11px 14px", borderRadius:9,
  border:"1px solid #333", background:"#1E1E1E",
  color:B.white, fontSize:14, fontFamily:"'DM Sans',sans-serif",
  outline:"none", boxSizing:"border-box",
};
const lbl = {
  fontSize:11, fontWeight:700, color:B.silver,
  letterSpacing:"0.09em", textTransform:"uppercase",
  display:"block", marginBottom:6, fontFamily:"'DM Sans',sans-serif",
};
const btn = (bg="#A51C30", disabled=false) => ({
  width:"100%", padding:"13px", borderRadius:9, border:"none",
  background: disabled ? "#333" : bg,
  color: disabled ? B.silver : B.white,
  cursor: disabled ? "not-allowed" : "pointer",
  fontSize:14, fontWeight:700, fontFamily:"'DM Sans',sans-serif",
  marginTop:8, transition:"opacity 0.15s",
});

// ─── Logo ─────────────────────────────────────────────────────────────────────
function LogoHeader({ brokerage }) {
  const name = brokerage?.name || "AKS Realty Group, LLC";
  return (
    <div style={{textAlign:"center", marginBottom:32}}>
      <div style={{
        width:76, height:76, borderRadius:"50%", overflow:"hidden",
        margin:"0 auto 16px", boxShadow:"0 4px 20px rgba(165,28,48,0.5)",
        background:B.red, display:"flex", alignItems:"center", justifyContent:"center",
      }}>
        {brokerage?.logoUrl
          ? <img src={brokerage.logoUrl} alt={name} style={{width:"100%", height:"100%", objectFit:"cover"}}/>
          : <span style={{fontSize:32}}>🏠</span>}
      </div>
      <h1 style={{margin:"0 0 4px", fontSize:22, fontWeight:800, color:B.white,
        fontFamily:"'Playfair Display',serif"}}>{name}</h1>
      <p style={{margin:0, color:B.silver, fontSize:11,
        letterSpacing:"0.1em", textTransform:"uppercase",
        fontFamily:"'DM Sans',sans-serif"}}>Transaction Management System</p>
    </div>
  );
}

// ─── Login Screen ─────────────────────────────────────────────────────────────
export function LoginScreen({ onLogin, brokerage, initialError }) {
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(initialError || "");
  const [showForgot, setShowForgot] = useState(false);

  if (showForgot) return <ForgotPasswordScreen onBack={()=>setShowForgot(false)} />;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email || !password) { setError("Please enter your email and password."); return; }
    setLoading(true); setError("");
    try {
      await signIn(email, password);
      // onLogin called via onAuthStateChange in App
    } catch (err) {
      setError(err.message || "Invalid email or password.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{minHeight:"100vh", background:B.black, display:"flex",
      flexDirection:"column", alignItems:"center", justifyContent:"center",
      padding:20, fontFamily:"'DM Sans',sans-serif"}}>
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet"/>
      <LogoHeader brokerage={brokerage}/>
      <form onSubmit={handleSubmit} style={{
        background:B.charcoal, borderRadius:14, padding:32,
        width:"100%", maxWidth:400, boxShadow:"0 20px 60px rgba(0,0,0,0.5)",
        border:"1px solid #2a2a2a",
      }}>
        <h2 style={{margin:"0 0 24px", fontSize:18, fontWeight:700,
          color:B.white, fontFamily:"'Playfair Display',serif"}}>Sign In</h2>

        {error && (
          <div style={{background:"#FFF5F5", border:"1px solid #FCA5A5",
            borderRadius:8, padding:"10px 14px", marginBottom:16,
            fontSize:13, color:"#DC2626", fontFamily:"'DM Sans',sans-serif"}}>
            {error}
          </div>
        )}

        <div style={{marginBottom:16}}>
          <label style={lbl}>Email Address</label>
          <input type="email" value={email} onChange={e=>setEmail(e.target.value)}
            style={inp} placeholder="you@aksrealtygroup.com" autoComplete="email"/>
        </div>
        <div style={{marginBottom:20}}>
          <label style={lbl}>Password</label>
          <input type="password" value={password} onChange={e=>setPassword(e.target.value)}
            style={inp} placeholder="••••••••" autoComplete="current-password"/>
        </div>

        <button type="submit" disabled={loading} style={btn(B.red, loading)}>
          {loading ? "Signing in…" : "Sign In"}
        </button>

        <div style={{textAlign:"center", marginTop:16}}>
          <button type="button" onClick={()=>setShowForgot(true)}
            style={{background:"none", border:"none", cursor:"pointer",
              fontSize:13, color:B.silver, fontFamily:"'DM Sans',sans-serif",
              textDecoration:"underline"}}>
            Forgot your password?
          </button>
        </div>
      </form>

      <p style={{marginTop:24, color:"#333", fontSize:11,
        fontFamily:"'DM Sans',sans-serif", textAlign:"center"}}>
        Contact your broker to get access{brokerage?.name ? " · "+brokerage.name : ""}
      </p>
    </div>
  );
}

// ─── Forgot Password Screen ───────────────────────────────────────────────────
export function ForgotPasswordScreen({ onBack }) {
  const [email,   setEmail]   = useState("");
  const [loading, setLoading] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email) { setError("Please enter your email address."); return; }
    setLoading(true); setError("");
    try {
      await resetPassword(email);
      setSent(true);
    } catch (err) {
      setError(err.message || "Could not send reset email.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{minHeight:"100vh", background:B.black, display:"flex",
      flexDirection:"column", alignItems:"center", justifyContent:"center",
      padding:20, fontFamily:"'DM Sans',sans-serif"}}>
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet"/>
      <LogoHeader/>
      <div style={{background:B.charcoal, borderRadius:14, padding:32,
        width:"100%", maxWidth:400, boxShadow:"0 20px 60px rgba(0,0,0,0.5)",
        border:"1px solid #2a2a2a"}}>
        <h2 style={{margin:"0 0 8px", fontSize:18, fontWeight:700,
          color:B.white, fontFamily:"'Playfair Display',serif"}}>Reset Password</h2>
        <p style={{margin:"0 0 24px", fontSize:13, color:B.silver,
          fontFamily:"'DM Sans',sans-serif"}}>
          Enter your email and we'll send you a reset link.
        </p>

        {sent ? (
          <div>
            <div style={{background:"#F0FDF4", border:"1px solid #86EFAC",
              borderRadius:10, padding:"16px", marginBottom:20,
              fontSize:13, color:"#166534", fontFamily:"'DM Sans',sans-serif",
              lineHeight:1.6}}>
              ✓ Reset email sent to <strong>{email}</strong>.<br/>
              Check your inbox and click the link to set a new password.
            </div>
            <button onClick={onBack} style={btn()}>Back to Sign In</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <div style={{background:"#FFF5F5", border:"1px solid #FCA5A5",
                borderRadius:8, padding:"10px 14px", marginBottom:16,
                fontSize:13, color:"#DC2626", fontFamily:"'DM Sans',sans-serif"}}>
                {error}
              </div>
            )}
            <div style={{marginBottom:20}}>
              <label style={lbl}>Email Address</label>
              <input type="email" value={email} onChange={e=>setEmail(e.target.value)}
                style={inp} placeholder="you@aksrealtygroup.com"/>
            </div>
            <button type="submit" disabled={loading} style={btn(B.red, loading)}>
              {loading ? "Sending…" : "Send Reset Link"}
            </button>
            <div style={{textAlign:"center", marginTop:12}}>
              <button type="button" onClick={onBack}
                style={{background:"none", border:"none", cursor:"pointer",
                  fontSize:13, color:B.silver, fontFamily:"'DM Sans',sans-serif",
                  textDecoration:"underline"}}>
                Back to Sign In
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Reset Password Screen (shown after clicking email link) ──────────────────
export function ResetPasswordScreen({ onDone }) {
  const [password,  setPassword]  = useState("");
  const [confirm,   setConfirm]   = useState("");
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");
  const [success,   setSuccess]   = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (password.length < 8)  { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm)  { setError("Passwords do not match."); return; }
    setLoading(true); setError("");
    try {
      await updatePassword(password);
      setSuccess(true);
      setTimeout(onDone, 2000);
    } catch (err) {
      setError(err.message || "Could not update password.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{minHeight:"100vh", background:B.black, display:"flex",
      flexDirection:"column", alignItems:"center", justifyContent:"center",
      padding:20, fontFamily:"'DM Sans',sans-serif"}}>
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet"/>
      <LogoHeader/>
      <div style={{background:B.charcoal, borderRadius:14, padding:32,
        width:"100%", maxWidth:400, boxShadow:"0 20px 60px rgba(0,0,0,0.5)",
        border:"1px solid #2a2a2a"}}>
        <h2 style={{margin:"0 0 8px", fontSize:18, fontWeight:700,
          color:B.white, fontFamily:"'Playfair Display',serif"}}>Set New Password</h2>
        <p style={{margin:"0 0 24px", fontSize:13, color:B.silver,
          fontFamily:"'DM Sans',sans-serif"}}>Choose a strong password for your account.</p>

        {success ? (
          <div style={{background:"#F0FDF4", border:"1px solid #86EFAC",
            borderRadius:10, padding:16, fontSize:13, color:"#166534",
            fontFamily:"'DM Sans',sans-serif"}}>
            ✓ Password updated! Redirecting to your dashboard…
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <div style={{background:"#FFF5F5", border:"1px solid #FCA5A5",
                borderRadius:8, padding:"10px 14px", marginBottom:16,
                fontSize:13, color:"#DC2626", fontFamily:"'DM Sans',sans-serif"}}>
                {error}
              </div>
            )}
            <div style={{marginBottom:16}}>
              <label style={lbl}>New Password</label>
              <input type="password" value={password} onChange={e=>setPassword(e.target.value)}
                style={inp} placeholder="Minimum 8 characters"/>
            </div>
            <div style={{marginBottom:20}}>
              <label style={lbl}>Confirm Password</label>
              <input type="password" value={confirm} onChange={e=>setConfirm(e.target.value)}
                style={inp} placeholder="Repeat your password"/>
            </div>
            {/* Password strength hints */}
            <div style={{marginBottom:16, display:"flex", gap:8, flexWrap:"wrap"}}>
              {[
                ["8+ chars",    password.length >= 8],
                ["Uppercase",   /[A-Z]/.test(password)],
                ["Number",      /[0-9]/.test(password)],
              ].map(([l,ok]) => (
                <span key={l} style={{fontSize:11, padding:"2px 10px", borderRadius:12,
                  background: ok ? "#F0FDF4" : "#1E1E1E",
                  color: ok ? "#16A34A" : B.silver,
                  border: `1px solid ${ok ? "#86EFAC" : "#333"}`,
                  fontFamily:"'DM Sans',sans-serif", fontWeight:700}}>
                  {ok ? "✓" : "○"} {l}
                </span>
              ))}
            </div>
            <button type="submit" disabled={loading} style={btn(B.red, loading)}>
              {loading ? "Updating…" : "Set New Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
